import React, { useEffect } from "react";
import {
  Input,
  Button,
  FormFeedback,
  Col,
  Row,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
} from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { countrySchema } from "@helpers/validation-schema";
import { NAME_MAXLENGTH } from "@constants/constants";
import { PHONE_NUMBER_MAXLENGTH } from "@constants/constants";
import { COUNTRY_MAX_LENGTH } from "@constants/constants";
import {
  addCountry,
  editCountry,
  getCountry,
  getCountryCount,
} from "../services/country-services";
import { resetError, resetState } from "../controller/country-slice";
import Spinners from "@components/custom-table/Spinner";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomLabel from "../../../components/CustomLabel";
import { handleInput } from "../../../helpers/helpers";
import { NAME_REGEX, NUMBER_REGEX } from "../../../constants/constants";

export const AddCountry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const route = pathname.split("/")[2];
  const countryId = pathname.split("/")[3];
  const success = useSelector((state) => state.country.success);
  const country = useSelector((state) => state.country.countries);
  const loading = useSelector((state) => state.country.loading);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pageQuery = parseInt(searchParams.get("current_page"));
  const successMessage = useSelector((state) => state.country.successMessage);
  const errorMessage = useSelector((state) => state.country.errorMessage);

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(countrySchema),
  });

  useEffect(() => {
    if (route === "add") {
      async function fetchCount() {
        const count = await getCountryCount();
        setValue("sorting", count + 1); // Set sorting in react-hook-form
      }
      fetchCount();
    }
  }, [setValue]);

  useEffect(() => {
    if (route !== "add") {
      dispatch(getCountry(countryId));
    }
  }, [countryId, dispatch, route]);

  useEffect(() => {
    if (success) {
      navigate(`/country?current_page=${pageQuery}`);
      dispatch(resetState());
    }
  }, [success, dispatch, navigate, successMessage, pageQuery]);

  useEffect(() => {
    if (errorMessage !== null) {
      toast.error(errorMessage?.message);

      errorMessage.data.items.forEach((item) => {
        const fieldName = Object.keys(item)[0];
        const message = item[fieldName];
        setError(fieldName, { type: "manual", message });
      });
    }
  }, [errorMessage, setError]);

  useEffect(() => {
    if (country) {
      setValue("name", country?.name);
      setValue("sorting", country?.sorting);
      setValue("phone_min", country?.phone_min);
      setValue("phone_max", country?.phone_max);
      setValue("phone_code", country.phone_code);
      setValue("short_country_code", country?.short_country_code);
      setValue("country_code", country?.country_code);
    }
  }, [country, setValue]);

  const onSubmit = (data) => {
    if (route !== "edit") {
      const formattedData = {
        ...data,
        sorting: parseInt(data.sorting),
        phone_min: parseInt(data.phone_min),
        phone_max: parseInt(data.phone_max),
        phone_code: parseInt(data.phone_code),
        is_india: false,
      };
      dispatch(addCountry(formattedData));
    } else {
      const formattedData = {
        ...data,
        sorting: parseInt(data.sorting),
        phone_min: parseInt(data.phone_min),
        phone_max: parseInt(data.phone_max),
        phone_code: parseInt(data.phone_code),
        is_india:
          country?.name?.trim().toLowerCase() === "india" ? true : false,
      };

      dispatch(editCountry({ id: countryId, data: formattedData }));
    }
  };

  return (
    <div className="page-content">
      {loading ? (
        <Spinners />
      ) : (
        <>
          <ToastContainer autoClose={2000} />
          <Card>
            <CardBody className="text-start">
              <CardTitle className="mb-4 my-3">
                {route === "edit"
                  ? "Edit Country"
                  : route === "view"
                  ? "View Country"
                  : "Create Country"}
              </CardTitle>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                    <CustomLabel htmlFor="Sorting" isRequired>
                        Sorting
                      </CustomLabel>
                      <Controller
                        name="sorting"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Sorting"
                              invalid={!!errors.sorting}
                              maxLength={PHONE_NUMBER_MAXLENGTH}
                              onInput={(e) => handleInput(e, NUMBER_REGEX)}
                            />
                            {errors.sorting && (
                              <FormFeedback>
                                {errors.sorting.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Name" isRequired>
                        Country
                      </CustomLabel>
                      <Controller
                        name="name"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Enter country name"
                              invalid={!!errors.name}
                              onInput={(e) => handleInput(e, NAME_REGEX)}
                              maxLength={NAME_MAXLENGTH}
                            />
                            {errors.name && (
                              <FormFeedback>{errors.name.message}</FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Phone Minimum" isRequired>
                        Phone Number Minimum
                      </CustomLabel>
                      <Controller
                        name="phone_min"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Enter Phone Number Minimum"
                              invalid={!!errors.phone_min}
                              maxLength={PHONE_NUMBER_MAXLENGTH}
                              onInput={(e) => handleInput(e, NUMBER_REGEX)}
                            />
                            {errors.phone_min && (
                              <FormFeedback>
                                {errors.phone_min.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Phone Maximum" isRequired>
                        Phone Number Maximum
                      </CustomLabel>
                      <Controller
                        name="phone_max"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Enter Phone Number Maximum"
                              onInput={(e) => handleInput(e, NUMBER_REGEX)}
                              invalid={!!errors.phone_max}
                              maxLength={PHONE_NUMBER_MAXLENGTH}
                            />
                            {errors.phone_max && (
                              <FormFeedback>
                                {errors.phone_max.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Phone Code" isRequired>
                        Phone Code
                      </CustomLabel>
                      <Controller
                        name="phone_code"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              onInput={(e) => handleInput(e, NUMBER_REGEX)}
                              placeholder="Enter Phone Code"
                              invalid={!!errors.phone_code}
                              maxLength={COUNTRY_MAX_LENGTH}
                            />
                            {errors.phone_code && (
                              <FormFeedback>
                                {errors.phone_code.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Short Country Code" isRequired>
                        Short Country Code
                      </CustomLabel>
                      <Controller
                        name="short_country_code"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Enter Short Country Code"
                              invalid={!!errors.short_country_code}
                              onInput={(e) => handleInput(e, NAME_REGEX)}
                              maxLength={PHONE_NUMBER_MAXLENGTH}
                            />
                            {errors.short_country_code && (
                              <FormFeedback>
                                {errors.short_country_code.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <CustomLabel htmlFor="Country Code" isRequired>
                        Country Code
                      </CustomLabel>
                      <Controller
                        name="country_code"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Enter Country Code"
                              onInput={(e) => handleInput(e, NAME_REGEX)}
                              invalid={!!errors.country_code}
                              maxLength={COUNTRY_MAX_LENGTH}
                            />
                            {errors.country_code && (
                              <FormFeedback>
                                {errors.country_code.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}></Col>
                  <Col md={6}>
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => {
                          navigate(`/country?current_page=${pageQuery}`);
                          dispatch(resetError());
                        }}
                      >
                        {route === "view" ? "Back" : "Cancel"}
                      </Button>
                      {route !== "view" ? (
                        <Button type="submit" color="primary">
                          {route === "edit" ? "Edit Country" : "Create Country"}
                        </Button>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

export default AddCountry;
